/**
 * Simulation API test utilities (canonical run/branch shape).
 *
 * Functions are exposed on window for quick browser debugging.
 */

import EnhancedForestAPI from '@/api/enhanced-forest';
import { logger } from '@/utils/debugLogger';

interface ApiErrorLike {
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

const simulationLogContext = { component: 'simulationTest' };

const toApiError = (error: unknown): ApiErrorLike => {
  if (error && typeof error === 'object') {
    return error as ApiErrorLike;
  }
  return { message: String(error) };
};

declare global {
  interface Window {
    testSimulationAPI: typeof testSimulationAPI;
    testSimulationRun: typeof testSimulationRun;
    testSimulationBranch: typeof testSimulationBranch;
  }
}

export const testSimulationAPI = async (
  propertyId: string = '491-543-4-38',
  standId: string = '24281417',
) => {
  logger.group('Testing Simulation API (canonical run/branch shape)');
  try {
    logger.info(simulationLogContext, 'Submitting run');
    const submission = await EnhancedForestAPI.submitStandSimulation(propertyId, standId, {
      years: 10,
      timestep: 5,
      decision_mode: 'NO_MANAGEMENT',
      include_carbon: true,
      include_economic: true,
    });

    logger.info(simulationLogContext, 'Submission successful', submission);
    const runId = submission.run_id;
    logger.info(simulationLogContext, 'Polling run status');
    for (let i = 1; i <= 5; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const run = await EnhancedForestAPI.getStandSimulationRun(propertyId, standId, runId);
        logger.info(simulationLogContext, `Run check ${i}`, {
          status: run.status,
          branches: run.branch_count,
        });
        if (run.status === 'COMPLETED') {
          const targetBranch = run.branches[0];
          const branch = await EnhancedForestAPI.getStandSimulationBranch(
            propertyId,
            standId,
            runId,
            targetBranch.branch_id,
          );
          logger.info(simulationLogContext, 'Branch fetched', {
            timeSeriesRows: branch.time_series.length,
            firstRow: branch.time_series[0],
            lastRow: branch.time_series[branch.time_series.length - 1],
          });
          logger.groupEnd();
          return { submission, run, branch };
        }
        if (run.status === 'FAILED') {
          logger.error(simulationLogContext, 'Simulation failed', run.error_message);
          logger.groupEnd();
          return { submission, run, error: run.error_message };
        }
      } catch (statusError) {
        const apiError = toApiError(statusError);
        logger.error(simulationLogContext, `Run check ${i} failed`, apiError.message);
        if (apiError.response) {
          logger.error(simulationLogContext, 'Response', apiError.response);
        }
      }
    }
    logger.warn(simulationLogContext, 'Polling timeout reached');
    logger.groupEnd();
    return { submission, timeout: true };
  } catch (error) {
    const apiError = toApiError(error);
    logger.error(simulationLogContext, 'Test failed', apiError.message);
    if (apiError.response) {
      logger.error(simulationLogContext, 'Response', apiError.response);
    }
    logger.groupEnd();
    throw error;
  }
};

export const testSimulationRun = async (
  propertyId: string,
  standId: string,
  runId: string,
) => {
  try {
    const run = await EnhancedForestAPI.getStandSimulationRun(propertyId, standId, runId);
    logger.info(simulationLogContext, 'Run fetched', run);
    return run;
  } catch (error) {
    const apiError = toApiError(error);
    logger.error(simulationLogContext, 'Run fetch failed', apiError.message);
    if (apiError.response) {
      logger.error(simulationLogContext, 'Response', apiError.response);
    }
    throw error;
  }
};

export const testSimulationBranch = async (
  propertyId: string,
  standId: string,
  runId: string,
  branchId: string,
) => {
  try {
    const branch = await EnhancedForestAPI.getStandSimulationBranch(
      propertyId,
      standId,
      runId,
      branchId,
    );
    logger.info(simulationLogContext, 'Branch fetched', branch);
    return branch;
  } catch (error) {
    const apiError = toApiError(error);
    logger.error(simulationLogContext, 'Branch fetch failed', apiError.message);
    if (apiError.response) {
      logger.error(simulationLogContext, 'Response', apiError.response);
    }
    throw error;
  }
};

if (typeof window !== 'undefined') {
  window.testSimulationAPI = testSimulationAPI;
  window.testSimulationRun = testSimulationRun;
  window.testSimulationBranch = testSimulationBranch;
}
